import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Download } from "lucide-react";
import { clsx } from "clsx";
import { useDownloadLicense, downloadLicenseFile } from "@/hooks/useLicenses";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
const STATUS_DOT = {
    ACTIVE: "bg-green-500",
    SUPERSEDED: "bg-gray-400",
    EXPIRED: "bg-red-500",
    REVOKED: "bg-red-700"
};
export function LicenseHistory({ licenses, customerId }) {
    const { canDownloadLicense } = usePermissions();
    const downloadMutation = useDownloadLicense();
    const toast = useToast();
    const historyLicenses = licenses.filter((l)=>l.Status !== "ACTIVE");
    if (historyLicenses.length === 0) return null;
    async function handleDownload(licenseId) {
        try {
            const blob = await downloadMutation.mutateAsync({
                licenseId,
                customerId
            });
            downloadLicenseFile(blob, licenseId);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to download license";
            toast.error(message);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-4 pt-3 border-t border-gray-100",
        children: [
            /*#__PURE__*/ _jsxs("h5", {
                className: "text-xs font-medium text-gray-500 mb-2",
                children: [
                    "License History (",
                    historyLicenses.length,
                    ")"
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1.5",
                children: historyLicenses.map((license)=>{
                    const dot = STATUS_DOT[license.Status] ?? STATUS_DOT["EXPIRED"];
                    const anchors = license.LicenseTerms.license_anchors ?? (license.LicenseTerms.cluster_anchor_serial ? [
                        license.LicenseTerms.cluster_anchor_serial
                    ] : []);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between rounded border border-gray-100 bg-gray-50/50 px-3 py-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: clsx("inline-block h-2 w-2 rounded-full", dot)
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-mono text-xs text-gray-600",
                                        children: anchors.length > 0 ? anchors.map((anchor)=>`${anchor.slice(0, 8)}...${anchor.slice(-8)}`).join(", ") : "Anchors unavailable"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-gray-400",
                                        children: license.Status
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-gray-400",
                                        children: new Date(license.IssuedAt).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric"
                                        })
                                    }),
                                    canDownloadLicense && license.LicenseFile && /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>handleDownload(license.LicenseId),
                                        className: "text-gray-400 hover:text-gray-600",
                                        title: "Download license file",
                                        children: /*#__PURE__*/ _jsx(Download, {
                                            className: "h-3.5 w-3.5"
                                        })
                                    })
                                ]
                            })
                        ]
                    }, license.LicenseId);
                })
            })
        ]
    });
}
